import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { supabaseOptimizer } from '@/utils/supabaseOptimizer';
import { authDebugger } from '@/utils/authDebugger';
import { getDefaultAuthenticatedRoute } from '@/domain/auth/authNavigation';

type AppRole = Database['public']['Enums']['app_role'];
type AccountStatus = 'pending' | 'approved' | 'suspended' | 'rejected';

interface RateLimitResult {
  allowed?: boolean;
  retryAfterMinutes?: number;
}

interface PasswordStrengthResult {
  meets_policy?: boolean;
  requirements?: unknown;
  score?: number;
  strength?: string;
}

interface LoginAttemptMetadata {
  failure_reason?: string;
}

const toError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error) return error;

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return new Error(message);
  }

  return new Error(fallbackMessage);
};

export interface UserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  roles: AppRole[]; // Roles array from user_roles table
  primaryRole: AppRole; // The primary role
  role: AppRole; // Backward compatibility alias for primaryRole
  account_status: AccountStatus;
  company_id?: string;
  avatar_url?: string;
  phone?: string;
  department?: string;
  created_at: string;
  updated_at: string;
  payment_status?: string;
  subscription_tier?: string;
  has_completed_checkout?: boolean;
  login_credentials_sent?: boolean;
  created_via?: string;
  // temporary_password removed for security — never stored in the database
  password_reset_required?: boolean;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  profileLoading: boolean;
  loading: boolean;
  error: string | null;
}

type AuthListener = (state: AuthState) => void;

class AuthService {
  private static instance: AuthService;
  private state: AuthState = {
    user: null,
    session: null,
    profile: null,
    profileLoading: true,
    loading: true,
    error: null
  };
  private listeners: Set<AuthListener> = new Set();
  private profileCache = new Map<string, { data: UserProfile; timestamp: number }>();
  private pendingProfileRequests = new Map<string, Promise<UserProfile | null>>();
  private initialized = false;
  
  // Circuit breaker for database calls
  private circuitBreaker = {
    failureCount: 0,
    lastFailureTime: 0,
    isOpen: false,
    threshold: 5,
    resetTimeoutMs: 30000 // 30 seconds
  };
  
  // Request coalescing for auth state changes
  private authStateChangeDebouncer: NodeJS.Timeout | null = null;
  private pendingAuthChange: Session | null = null;

  private constructor() {
    this.initializeAuth();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private async initializeAuth() {
    try {
      authDebugger.logAuthStep('Initializing AuthService', {
        environment: authDebugger.getEnvironmentInfo(),
        timestamp: new Date().toISOString()
      });

      // Set up auth listener first
      supabase.auth.onAuthStateChange(async (event, session) => {
        authDebugger.logAuthStep('Auth state change event', { event, hasSession: !!session });
        await this.handleAuthChange(session);
      });

      // Check for existing session
      authDebugger.logAuthStep('Checking for existing session');
      const { data: { session } } = await supabase.auth.getSession();
      authDebugger.logAuthStep('Session check complete', { hasSession: !!session });
      await this.handleAuthChange(session);
      
      this.initialized = true;
      authDebugger.logAuthStep('AuthService initialization complete');
    } catch (error) {
      console.error('Auth initialization failed:', error);
      authDebugger.logAuthError(error, 'Auth initialization failed');
      this.updateState({
        loading: false,
        profileLoading: false,
        error: 'Failed to initialize authentication',
      });
    }
  }

  private async handleAuthChange(session: Session | null) {
    // Debounce rapid auth state changes to prevent race conditions
    if (this.authStateChangeDebouncer) {
      clearTimeout(this.authStateChangeDebouncer);
    }
    
    this.pendingAuthChange = session;
    this.authStateChangeDebouncer = setTimeout(() => {
      this.processAuthStateChange(this.pendingAuthChange);
    }, 100); // 100ms debounce
  }

  private async processAuthStateChange(session: Session | null) {
    if (!session?.user) {
      this.clearAuthState();
      return;
    }

    // Set user immediately for fast UI updates
    this.updateState({
      user: session.user,
      session,
      profileLoading: true,
      loading: false,
      error: null
    });

    // Fetch profile in background with circuit breaker
    try {
      const profile = await this.fetchUserProfileWithCircuitBreaker(session.user.id);
      this.updateState({ profile, profileLoading: false });
    } catch (error) {
      console.error('Profile fetch failed:', error);
      // Don't set error state - user can still use the app with basic functionality
      this.updateState({
        profile: this.createFallbackProfile(session.user),
        profileLoading: false,
      });
    }
  }

  private createFallbackProfile(user: User): UserProfile {
    return {
      id: user.id,
      email: user.email!,
      first_name: user.user_metadata?.first_name || '',
      last_name: user.user_metadata?.last_name || '',
      roles: ['user' as AppRole],
      primaryRole: 'user' as AppRole,
      role: 'user' as AppRole, // Backward compatibility
      account_status: 'pending' as AccountStatus,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  private async fetchUserProfileWithCircuitBreaker(userId: string): Promise<UserProfile | null> {
    // Check if circuit breaker is open
    if (this.isCircuitBreakerOpen()) {
      console.warn('[AuthService] Circuit breaker is open, skipping profile fetch');
      throw new Error('Circuit breaker is open');
    }

    try {
      const profile = await this.fetchUserProfile(userId);
      this.onCircuitBreakerSuccess();
      return profile;
    } catch (error) {
      this.onCircuitBreakerFailure();
      throw error;
    }
  }

  private isCircuitBreakerOpen(): boolean {
    if (!this.circuitBreaker.isOpen) return false;
    
    // Check if we should reset the circuit breaker
    const now = Date.now();
    if (now - this.circuitBreaker.lastFailureTime > this.circuitBreaker.resetTimeoutMs) {
      this.circuitBreaker.isOpen = false;
      this.circuitBreaker.failureCount = 0;
      console.log('[AuthService] Circuit breaker reset');
    }
    
    return this.circuitBreaker.isOpen;
  }

  private onCircuitBreakerSuccess(): void {
    this.circuitBreaker.failureCount = 0;
    this.circuitBreaker.isOpen = false;
  }

  private onCircuitBreakerFailure(): void {
    this.circuitBreaker.failureCount++;
    this.circuitBreaker.lastFailureTime = Date.now();
    
    if (this.circuitBreaker.failureCount >= this.circuitBreaker.threshold) {
      this.circuitBreaker.isOpen = true;
      console.warn('[AuthService] Circuit breaker opened due to repeated failures');
    }
  }

  private async fetchUserProfile(userId: string): Promise<UserProfile | null> {
    // Check cache first
    const cached = this.profileCache.get(userId);
    if (cached && Date.now() - cached.timestamp < 300000) { // 5 minutes
      return cached.data;
    }

    // Check if there's already a pending request for this user
    const existingRequest = this.pendingProfileRequests.get(userId);
    if (existingRequest) {
      console.log('[AuthService] Using existing profile request');
      return existingRequest;
    }

    // Create new request with deduplication
    const profileRequest = this.performProfileFetch(userId);
    this.pendingProfileRequests.set(userId, profileRequest);
    
    try {
      const profile = await profileRequest;
      return profile;
    } finally {
      // Clean up the pending request
      this.pendingProfileRequests.delete(userId);
    }
  }

  private async performProfileFetch(userId: string): Promise<UserProfile | null> {
    const retryConfig = { maxRetries: 2, baseDelay: 300 };
    
    for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
      try {
        const timeoutMs = 500;
        
        // Fetch profile data
        const result = await supabaseOptimizer.optimizedProfileQuery(userId, timeoutMs);

        if (result.error) {
          console.warn(`[AuthService] Database error on attempt ${attempt + 1}:`, result.error);
          throw result.error;
        }
        
        if (!result.data) {
          console.warn(`[AuthService] No profile found for user ${userId}`);
          return null;
        }

        // SECURITY: Fetch roles from user_roles table (not profiles.role)
        const { data: userRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .is('revoked_at', null);

        if (rolesError) {
          console.warn(`[AuthService] Failed to fetch user roles:`, rolesError);
        }

        const roles = (userRoles || []).map(r => r.role as AppRole);
        const primaryRole = roles.includes('super_admin' as AppRole) ? 'super_admin' as AppRole
          : roles.includes('admin' as AppRole) ? 'admin' as AppRole
          : roles.includes('manager' as AppRole) ? 'manager' as AppRole
          : roles[0] || 'user' as AppRole;

        const profile: UserProfile = {
          ...result.data,
          roles,
          primaryRole,
          role: primaryRole // Backward compatibility
        };
        
        this.profileCache.set(userId, { data: profile, timestamp: Date.now() });
        console.log(`[AuthService] Profile fetched successfully with roles from user_roles table`);
        return profile;
      } catch (error) {
        const isLastAttempt = attempt === retryConfig.maxRetries;
        
        if (isLastAttempt) {
          console.error(`[AuthService] Profile fetch failed after ${retryConfig.maxRetries + 1} attempts:`, error);
          return null;
        }
        
        const delay = retryConfig.baseDelay * Math.pow(1.5, attempt) + Math.random() * 100;
        console.warn(`[AuthService] Attempt ${attempt + 1} failed, retrying in ${Math.round(delay)}ms:`, error);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    return null;
  }

  private updateState(updates: Partial<AuthState>) {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  }

  private clearAuthState() {
    this.profileCache.clear();
    this.pendingProfileRequests.clear();
    this.updateState({
      user: null,
      session: null,
      profile: null,
      profileLoading: false,
      loading: false,
      error: null
    });
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.state));
  }

  // Public API
  getState(): AuthState {
    return { ...this.state };
  }

  subscribe(listener: AuthListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async signIn(email: string, password: string) {
    try {
      // CRITICAL SECURITY: Validate inputs
      if (!email || !password) {
        const error = new Error('Email and password are required');
        this.updateState({ loading: false, error: error.message });
        return { data: null, error };
      }

      // Auth attempt logged to authDebugger only (no console logging of sensitive data)

      const envInfo = authDebugger.getEnvironmentInfo();
      const isExternalDomain = window.location.hostname === 'www.dialgia.com';
      
      // Clean up potentially stale auth state for external domains
      if (isExternalDomain) {
        console.log('[AuthService] External domain login - cleaning up stale state');
        this.cleanupAuthState();
      }
      
      authDebugger.logAuthStep('Starting sign in process', { 
        email, 
        environment: envInfo,
        isExternalDomain
      });
      
      this.updateState({ loading: true, error: null });
      
      // Enhanced rate limiting check
      authDebugger.logAuthStep('Checking rate limits');
      try {
        const rateLimitResult = await supabase.functions.invoke('check-login-rate-limit', {
          body: { action: 'login' }
        });

        if (rateLimitResult.data && !rateLimitResult.error) {
          const result = rateLimitResult.data as RateLimitResult;
          authDebugger.logAuthStep('Rate limit check result', result);
          if (!result.allowed) {
            const error = new Error(
              `Too many login attempts. Please try again in ${Math.ceil(result.retryAfterMinutes || 5)} minutes.`
            );
            authDebugger.logAuthError(error, 'Rate limit exceeded');
            this.updateState({ loading: false, error: error.message });
            return { data: null, error };
          }
        } else {
          console.warn('[AuthService] Rate limiting check failed, proceeding without rate limit');
        }
      } catch (rateLimitError) {
        console.warn('[AuthService] Rate limiting error:', rateLimitError);
        // Continue without rate limiting on database errors
      }

      // Attempting authentication
      authDebugger.logAuthStep('Attempting Supabase sign in');

      // CRITICAL: Actual Supabase authentication - THIS IS THE SECURITY GATE
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password,
      });

      // Response received (logged to authDebugger without sensitive details)
      authDebugger.logAuthStep('Sign in response received', { 
        hasData: !!data, 
        hasError: !!error,
        sessionExists: !!data?.session,
        userExists: !!data?.user
      });

      // Log login attempt for security monitoring
      try {
        await this.logLoginAttempt(email, !error, {
          failure_reason: error?.message
        });
      } catch (logError) {
        console.warn('[AuthService] Failed to log login attempt:', logError);
      }

      if (error) {
        authDebugger.logAuthError(error, 'Supabase sign in failed');
        throw error;
      }
      
      authDebugger.logAuthStep('Sign in successful');
      return { data, error: null };
    } catch (error: unknown) {
      const normalizedError = toError(error, 'Sign in failed');
      authDebugger.logAuthError(normalizedError, 'Sign in process failed');
      this.updateState({ loading: false, error: normalizedError.message });
      return { data: null, error: normalizedError };
    }
  }

  async signUp(email: string, password: string, metadata?: Record<string, unknown>) {
    try {
      this.updateState({ loading: true, error: null });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: metadata || {}
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error: unknown) {
      const normalizedError = toError(error, 'Sign up failed');
      this.updateState({ loading: false, error: normalizedError.message });
      return { data: null, error: normalizedError };
    }
  }

  async signOut() {
    try {
      this.updateState({ loading: true });
      
      // Clean up all auth state first
      this.cleanupAuthState();
      
      // Attempt global sign out
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
        console.warn('Global signout failed:', err);
      }
      
      // Force page reload for clean state
      window.location.href = '/auth';
    } catch (error: unknown) {
      console.error('Error signing out:', error);
      // Still try to clear state and redirect
      this.clearAuthState();
      window.location.href = '/auth';
    }
  }
  
  private cleanupAuthState() {
    // Clear profile cache
    this.profileCache.clear();
    
    // Clear all localStorage auth keys
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear sessionStorage auth keys if in use
    try {
      Object.keys(sessionStorage || {}).forEach((key) => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (err) {
      // Ignore sessionStorage errors
    }
  }

  async resetPassword(email: string) {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/password-reset`
      });
      return { error };
    } catch (error: unknown) {
      return { error: toError(error, 'Password reset request failed') };
    }
  }

  async updatePassword(password: string) {
    try {
      // Validate password strength using database function
      const { data: strengthResult, error: strengthError } = await supabase.rpc(
        'validate_password_strength', 
        { password_text: password }
      );

      if (strengthError) {
        return { data: null, error: strengthError };
      }

      const result = strengthResult as PasswordStrengthResult;
      if (!result?.meets_policy) {
        return { 
          data: null, 
          error: new Error(`Password does not meet security requirements: ${JSON.stringify(result?.requirements)}`)
        };
      }

      const { data, error } = await supabase.auth.updateUser({ password });
      
      if (!error && this.state.user?.id) {
        // Log security event
        await supabase.functions.invoke('log-security-event', {
          body: {
            eventType: 'password_changed',
            eventData: {
              strength_score: result?.score,
              strength_level: result?.strength
            },
            riskLevel: 'low'
          }
        });
      }

      return { data, error };
    } catch (error: unknown) {
      return { data: null, error: toError(error, 'Password update failed') };
    }
  }

  private async logLoginAttempt(
    email: string,
    success: boolean,
    metadata: LoginAttemptMetadata = {},
  ) {
    try {
      await supabase.functions.invoke('record-login-attempt', {
        body: {
          email,
          success,
          failureReason: metadata.failure_reason
        }
      });
    } catch (error) {
      console.warn('Failed to log login attempt:', error);
    }
  }

  /**
   * Check if user has admin-level access (UI-ONLY)
   * 
   * SECURITY NOTE: This method is for UI display purposes only.
   * All actual authorization decisions MUST be enforced server-side via:
   * - RLS policies on database tables
   * - Server-side role validation in Edge Functions
   * - JWT claims verification
   * 
   * Do NOT rely on this method for security-critical decisions.
   * Client-side state can be manipulated through browser dev tools.
   */
  hasAdminAccess(): boolean {
    if (!this.state.profile?.roles || this.state.profile.roles.length === 0) return false;
    
    const hasAdminRole = this.state.profile.roles.some(role => 
      ['super_admin', 'admin', 'manager'].includes(role)
    );
    
    if (!hasAdminRole) return false;
    
    // Super admins and admins need approval
    if (this.state.profile.roles.some(role => ['super_admin', 'admin'].includes(role))) {
      return this.state.profile.account_status === 'approved';
    }
    
    // Managers created via checkout are automatically approved
    if (this.state.profile.roles.includes('manager' as AppRole)) {
      return this.state.profile.account_status === 'approved' || 
             this.state.profile.created_via === 'checkout';
    }
    
    return false;
  }

  isApproved(): boolean {
    return this.state.profile?.account_status === 'approved';
  }

  isPending(): boolean {
    return this.state.profile?.account_status === 'pending';
  }

  refreshProfile() {
    if (this.state.user) {
      this.profileCache.delete(this.state.user.id);
      this.fetchUserProfile(this.state.user.id).then(profile => {
        this.updateState({ profile });
      });
    }
  }

  // Check if user has completed checkout and payment
  hasCompletedCheckout(): boolean {
    return this.state.profile?.has_completed_checkout === true;
  }

  // Check if user has full access (approved + completed checkout)
  hasFullAccess(): boolean {
    return this.isApproved() && (this.hasCompletedCheckout() || this.hasAdminAccess());
  }

  // Check if user needs to complete checkout
  needsCheckoutCompletion(): boolean {
    return this.state.profile?.created_via === 'checkout' && !this.hasCompletedCheckout();
  }

  // Route helpers
  getDefaultRoute(): string {
    if (!this.state.user) return '/auth';
    return getDefaultAuthenticatedRoute(this.state.profile);
  }
}

export default AuthService;
